import React from 'react';
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '../../hooks/useSubscription';

const UsageIndicator = ({
  feature,
  showDetails = true,
  showTotals = true,
  className = '',
  size = 'default', // 'small', 'default', 'large'
}) => {
  const { currentUsage, getUpgradeForFeature, getEntitlementUsage, FEATURES } = useSubscription();

  const totalMockups = currentUsage.totalMockups || 0;
  const totalMockupImages = currentUsage.totalMockupImages || 0;

  // Entitlement-based usage metrics
  const exportUsage = getEntitlementUsage('exports_per_month');
  const etsyStoreUsage = getEntitlementUsage('etsy_store_limit');
  const shopifyStoreUsage = getEntitlementUsage('shopify_store_limit');
  const mockupUsage = getEntitlementUsage('monthly_mockups');
  const seatUsage = getEntitlementUsage('user_seat_limit');

  const getProgressColor = pct => {
    if (pct >= 100) return 'bg-red-500';
    if (pct >= 90) return 'bg-red-400';
    if (pct >= 70) return 'bg-amber-400';
    return 'bg-green-500';
  };

  const getTextColor = pct => {
    if (pct >= 90) return 'text-red-600';
    if (pct >= 70) return 'text-amber-600';
    return 'text-sage-600';
  };

  const upgradeInfo = getUpgradeForFeature(FEATURES.BATCH_MOCKUPS);

  const UsageBar = ({ label, used, limit, className: barClassName = '' }) => {
    if (limit === undefined || limit === null) return null;
    const isUnlimited = limit === -1;
    const pct = isUnlimited ? 0 : limit > 0 ? (used / limit) * 100 : 0;
    const remaining = isUnlimited ? Infinity : Math.max(0, limit - used);

    return (
      <div className={barClassName}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-sage-700">{label}</span>
          <span className={`text-xs font-semibold ${isUnlimited ? 'text-sage-500' : getTextColor(pct)}`}>
            {isUnlimited ? `${used} used` : `${used} / ${limit}`}
          </span>
        </div>
        {!isUnlimited && (
          <div className="w-full bg-sage-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-500 ${getProgressColor(pct)}`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
        )}
        {!isUnlimited && pct >= 90 && (
          <p className="text-[10px] text-red-500 mt-0.5">
            {remaining === 0 ? 'Limit reached' : `${remaining} remaining`}
          </p>
        )}
      </div>
    );
  };

  if (size === 'small') {
    return (
      <div className={`text-xs ${className}`}>
        <div className="flex items-center space-x-3">
          {exportUsage && exportUsage.limit !== -1 && (
            <>
              <div className="flex-1 bg-sage-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor(exportUsage.percentage)}`}
                  style={{ width: `${Math.min(exportUsage.percentage, 100)}%` }}
                />
              </div>
              <span className={`font-medium ${getTextColor(exportUsage.percentage)}`}>
                {exportUsage.remaining === Infinity ? 'Unlimited' : `${exportUsage.remaining} exports left`}
              </span>
            </>
          )}
          {showTotals && (
            <span className="text-sage-500">
              {totalMockups} sets &middot; {totalMockupImages} images
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-sage-200 p-5 ${className}`}>
      {/* Totals */}
      {showTotals && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <div className="bg-sage-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-sage-900">{totalMockups}</div>
            <div className="text-xs text-sage-500">Mockup Sets</div>
          </div>
          <div className="bg-sage-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-sage-900">{totalMockupImages}</div>
            <div className="text-xs text-sage-500">Mockup Images</div>
          </div>
        </div>
      )}

      {/* Entitlement usage bars */}
      {showDetails && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-sage-800">Usage This Month</h4>

          {exportUsage && <UsageBar label="Exports" used={exportUsage.used} limit={exportUsage.limit} />}

          {mockupUsage && <UsageBar label="Mockups" used={mockupUsage.used} limit={mockupUsage.limit} />}

          {etsyStoreUsage && <UsageBar label="Etsy Stores" used={etsyStoreUsage.used} limit={etsyStoreUsage.limit} />}

          {shopifyStoreUsage && shopifyStoreUsage.limit > 0 && (
            <UsageBar label="Shopify Stores" used={shopifyStoreUsage.used} limit={shopifyStoreUsage.limit} />
          )}

          {seatUsage && <UsageBar label="User Seats" used={seatUsage.used} limit={seatUsage.limit} />}

          {/* Upgrade nudge when close to limits */}
          {exportUsage && exportUsage.limit !== -1 && exportUsage.percentage >= 70 && upgradeInfo && (
            <div className="bg-lavender-50 border border-lavender-200 rounded-xl p-3 mt-3">
              <div className="flex items-start space-x-2">
                <ArrowUpCircleIcon className="w-5 h-5 text-lavender-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-lavender-800 mb-2">
                    {exportUsage.percentage >= 90
                      ? `You've nearly reached your export limit. Upgrade to ${upgradeInfo.config.name} for more.`
                      : `Running low on exports? Upgrade to ${upgradeInfo.config.name} for more capacity.`}
                  </p>
                  <button className="text-xs bg-lavender-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-lavender-700 transition-colors">
                    Upgrade to {upgradeInfo.config.name}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageIndicator;
